import React, { useState } from "react";
import {
  Modal,
  Table,
  Upload,
  Input,
  Button,
  Popconfirm,
  Tooltip,
  message,
} from "antd";

import Papa from "papaparse";

import {
  Trash2,
  Pencil,
  Save,
  X,
  FileQuestionMark,
  CircleQuestionMark,
} from "lucide-react";

function BatchCsvUpload({
  children,
  createNew,
  setOpen,
  open,
  type = "BATCH",
}) {
  const [tableData, setTableData] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [hasErrors, setHasErrors] = useState(false);

  // Validate all rows
  const validateData = (data) => {
    if (type === "STUDENT") {
      const invalidRows = data.some(
        (item) =>
          !item.firstName?.trim() ||
          !item.lastName?.trim() ||
          !item.email?.trim(),
      );
      setHasErrors(invalidRows);
      return !invalidRows;
    } else {
      const invalidRows = data.some((item) => !item.batchName?.trim());
      setHasErrors(invalidRows);
      return !invalidRows;
    }
  };

  const handleFileUpload = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        let formattedData;

        if (type === "STUDENT") {
          formattedData = results.data.map((item, index) => ({
            key: index,
            serial: index + 1,
            firstName: item["First Name"] || item["firstName"] || "",
            lastName: item["Last Name"] || item["lastName"] || "",
            email: item["Email"] || item["email"] || "",
            studentId:
              item["Student ID"] ||
              item["studentId"] ||
              item["Student Id"] ||
              "",
          }));
        } else {
          formattedData = results.data.map((item, index) => ({
            key: index,
            serial: index + 1,
            batchName: item["Batch Name"] || "",
            tags: [
              item["Tag 1"],
              item["Tag 2"],
              item["Tag 3"],
              item["Tag 4"],
              item["Tag 5"],
              item["Tag 6"],
            ]
              .filter(Boolean)
              .join(", "),
            description: item["Description"] || "",
          }));
        }

        setTableData(formattedData);
        validateData(formattedData);
        setOpen(true);
      },
    });

    return false;
  };

  const handleChange = (value, key, field) => {
    const updatedData = tableData.map((item) =>
      item.key === key
        ? {
            ...item,
            [field]: value,
          }
        : item,
    );

    setTableData(updatedData);
    validateData(updatedData);
  };

  const handleDelete = (key) => {
    const filteredData = tableData
      .filter((item) => item.key !== key)
      .map((item, index) => ({
        ...item,
        serial: index + 1,
      }));

    setTableData(filteredData);
    validateData(filteredData);
  };

  const handleTableData = (tableData) => {
    if (!validateData(tableData)) {
      message.error("Please fill all required fields");
      return;
    }

    let updatedTableData;

    if (type === "STUDENT") {
      updatedTableData = tableData.map((item) => ({
        firstName: item.firstName,
        lastName: item.lastName,
        emailId: item.email,
        studentId: item.studentId,
        mobileNumber: null,
        countryCode: null,
      }));
    } else {
      updatedTableData = tableData.map((item) => ({
        batchName: item.batchName,
        tags:
          typeof item.tags === "string"
            ? item.tags
                .split(",")
                .map((tag) => tag.trim())
                .filter(Boolean)
            : item.tags,
        description: item.description,
      }));
    }

    createNew(updatedTableData);
  };

  // Define columns based on type
  const getColumns = () => {
    if (type === "STUDENT") {
      return [
        {
          title: "S.No",
          dataIndex: "serial",
          width: 60,
        },
        {
          title: (
            <span>
              First Name <span className="text-red-500">*</span>
            </span>
          ),
          dataIndex: "firstName",
          width: 150,
          render: (_, record) =>
            editingKey === record.key ? (
              <Input
                value={record.firstName}
                onChange={(e) =>
                  handleChange(e.target.value, record.key, "firstName")
                }
                placeholder="Enter first name"
                status={!record.firstName?.trim() ? "error" : ""}
              />
            ) : (
              <span className={!record.firstName?.trim() ? "text-red-500" : ""}>
                {record.firstName || "—"}
              </span>
            ),
        },
        {
          title: (
            <span>
              Last Name <span className="text-red-500">*</span>
            </span>
          ),
          dataIndex: "lastName",
          width: 150,
          render: (_, record) =>
            editingKey === record.key ? (
              <Input
                value={record.lastName}
                onChange={(e) =>
                  handleChange(e.target.value, record.key, "lastName")
                }
                placeholder="Enter last name"
                status={!record.lastName?.trim() ? "error" : ""}
              />
            ) : (
              <span className={!record.lastName?.trim() ? "text-red-500" : ""}>
                {record.lastName || "—"}
              </span>
            ),
        },
        {
          title: (
            <span>
              Email <span className="text-red-500">*</span>
            </span>
          ),
          dataIndex: "email",
          width: 250,
          render: (_, record) =>
            editingKey === record.key ? (
              <Input
                value={record.email}
                onChange={(e) =>
                  handleChange(e.target.value, record.key, "email")
                }
                placeholder="Enter email"
                type="email"
                status={!record.email?.trim() ? "error" : ""}
              />
            ) : (
              <span className={!record.email?.trim() ? "text-red-500" : ""}>
                {record.email || "—"}
              </span>
            ),
        },
        {
          title: "Student ID",
          dataIndex: "studentId",
          width: 150,
          render: (_, record) =>
            editingKey === record.key ? (
              <Input
                value={record.studentId}
                onChange={(e) =>
                  handleChange(e.target.value, record.key, "studentId")
                }
                placeholder="Enter student ID (optional)"
              />
            ) : (
              record.studentId || "—"
            ),
        },
        {
          title: "Action",
          width: 120,
          render: (_, record) => {
            const isEditing = editingKey === record.key;

            return (
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <Button
                    type="primary"
                    icon={<Save size={16} />}
                    onClick={() => setEditingKey(null)}
                  />
                ) : (
                  <Button
                    icon={<Pencil size={16} />}
                    onClick={() => setEditingKey(record.key)}
                  />
                )}

                <Popconfirm
                  title="Delete row?"
                  onConfirm={() => handleDelete(record.key)}
                >
                  <Button danger icon={<Trash2 size={16} />} />
                </Popconfirm>
              </div>
            );
          },
        },
      ];
    } else {
      // BATCH type columns
      return [
        {
          title: "S.No",
          dataIndex: "serial",
          width: 60,
        },
        {
          title: (
            <span>
              Batch Name <span className="text-red-500">*</span>
            </span>
          ),
          dataIndex: "batchName",
          width: 220,
          render: (_, record) =>
            editingKey === record.key ? (
              <Input
                value={record.batchName}
                onChange={(e) =>
                  handleChange(e.target.value, record.key, "batchName")
                }
                placeholder="Enter batch name"
                status={!record.batchName?.trim() ? "error" : ""}
              />
            ) : (
              <span className={!record.batchName?.trim() ? "text-red-500" : ""}>
                {record.batchName || "—"}
              </span>
            ),
        },
        {
          title: (
            <div className="flex items-center gap-1">
              <span>Tags</span>{" "}
              <Tooltip title="Give comma-separated tags and only first 6 tags will be considered. ">
                <CircleQuestionMark size={15} />
              </Tooltip>
            </div>
          ),
          dataIndex: "tags",
          width: 300,
          render: (_, record) =>
            editingKey === record.key ? (
              <Input
                value={record.tags}
                onChange={(e) =>
                  handleChange(e.target.value, record.key, "tags")
                }
                placeholder="Comma separated tags"
              />
            ) : (
              record.tags
            ),
        },
        {
          title: "Description",
          dataIndex: "description",
          render: (_, record) =>
            editingKey === record.key ? (
              <Input.TextArea
                value={record.description}
                onChange={(e) =>
                  handleChange(e.target.value, record.key, "description")
                }
                autoSize={{ minRows: 1, maxRows: 4 }}
              />
            ) : (
              record.description
            ),
        },
        {
          title: "Action",
          width: 160,
          render: (_, record) => {
            const isEditing = editingKey === record.key;

            return (
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <Button
                    type="primary"
                    icon={<Save size={16} />}
                    onClick={() => setEditingKey(null)}
                  />
                ) : (
                  <Button
                    icon={<Pencil size={16} />}
                    onClick={() => setEditingKey(record.key)}
                  />
                )}

                <Popconfirm
                  title="Delete row?"
                  onConfirm={() => handleDelete(record.key)}
                >
                  <Button danger icon={<Trash2 size={16} />} />
                </Popconfirm>
              </div>
            );
          },
        },
      ];
    }
  };

  const columns = getColumns();

  // Check if submit should be disabled
  const isSubmitDisabled = editingKey !== null || hasErrors;

  return (
    <>
      <Upload
        accept=".csv"
        showUploadList={false}
        beforeUpload={handleFileUpload}
      >
        {children}
      </Upload>

      <Modal
        title=<div className="text-[#0F1B23] font-semibold">
          {type === "STUDENT" ? "Uploaded Student Data" : "Uploaded Batch Data"}
        </div>
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={1200}
        centered
      >
        {hasErrors && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            ⚠️ Please fill in all required fields (marked with *)
          </div>
        )}

        <Table
          className="mt-4"
          columns={columns}
          dataSource={tableData}
          pagination={false}
          bordered
          scroll={{ x: type === "STUDENT" ? 900 : 1100 }}
        />

        <div className="mt-5 flex sm:flex-row flex-col sm:justify-end gap-x-3 gap-y-1">
          <button
            onClick={() => setOpen(false)}
            className="btn-white px-3 py-2 rounded-md sm:w-[200px] w-full"
          >
            Cancel
          </button>
          <button
            disabled={isSubmitDisabled}
            onClick={() => handleTableData(tableData)}
            className={`${
              isSubmitDisabled
                ? "btn-disabled opacity-50 cursor-not-allowed"
                : "btn-orange"
            } px-3 py-2 rounded-md sm:w-[200px] w-full`}
          >
            {type === "STUDENT" ? "Add Students" : "Create Batch"}
          </button>
        </div>
      </Modal>
    </>
  );
}

export default BatchCsvUpload;
